/**
 * DepartmentsPanel — CRUD for per-org Departments.
 *
 * Phase 5 (Jun 29 2026): mounted inside FieldSettingsTab. Mirrors the
 * AttendanceStatusesPanel pattern for visual consistency. Delete is
 * BLOCKED by the backend when the department is still referenced by any
 * Batch — surfaced as a clear "Deactivate instead" toast.
 */
import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Pencil, Plus, Power, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const EMPTY = { name: '', code: '', description: '' };

export default function DepartmentsPanel() {
  const { get, post, put, del } = useApi();
  const { hasRole } = useAuth();
  const canEdit = hasRole('org_admin');

  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [reorderBusy, setReorderBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await get('/trainees/departments?include_inactive=true');
      setDepartments(res.departments || []);
    } catch {
      toast.error('Failed to load departments');
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpenDialog(true); };
  const openEdit = (d) => {
    setEditing(d);
    setForm({ name: d.name, code: d.code, description: d.description || '' });
    setOpenDialog(true);
  };

  const onSave = async () => {
    if (!form.name.trim() || !form.code.trim()) {
      toast.error('Name and code are required'); return;
    }
    try {
      if (editing) {
        await put(`/trainees/departments/${editing.department_id}`, form);
        toast.success('Department updated');
      } else {
        await post('/trainees/departments', form);
        toast.success('Department added');
      }
      setOpenDialog(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const onDelete = async () => {
    if (!confirmDelete) return;
    try {
      await del(`/trainees/departments/${confirmDelete.department_id}`);
      toast.success('Department deleted');
      setConfirmDelete(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const toggleActive = async (d) => {
    try {
      await put(`/trainees/departments/${d.department_id}`, { is_active: !d.is_active });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const moveRow = async (i, dir) => {
    if (reorderBusy) return;
    const t = i + dir;
    if (t < 0 || t >= departments.length) return;
    const next = [...departments];
    [next[i], next[t]] = [next[t], next[i]];
    setDepartments(next);
    setReorderBusy(true);
    try {
      await post('/trainees/departments/reorder', {
        department_ids: next.map(d => d.department_id),
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reorder failed');
      await load();
    } finally { setReorderBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="departments-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-display text-base font-semibold">Departments</h3>
          <p className="text-xs text-muted-foreground">
            Define the departments your batches roll up to. Codes are used as the batch-code prefix (e.g. <code className="text-[10px]">ROB-B1</code>).
          </p>
        </div>
        {canEdit && (
          <Button onClick={openCreate} size="sm" data-testid="create-department-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Department
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : departments.length === 0 ? (
        <p className="text-sm text-muted-foreground">No departments yet. Add one to start grouping batches.</p>
      ) : (
        <Card className="divide-y">
          {departments.map((d, idx) => (
            <div key={d.department_id} className="flex items-center gap-3 p-3 hover:bg-muted/40"
                 data-testid={`department-row-${d.department_id}`}>
              <span className="inline-flex w-12 h-7 rounded bg-[var(--lumyn-content)]/10 text-[var(--lumyn-content)] text-xs font-bold items-center justify-center">
                {d.code}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{d.name}</span>
                  {!d.is_active && <Badge variant="secondary" className="text-[10px]">Inactive</Badge>}
                  {d.batch_count > 0 && (
                    <Badge variant="outline" className="text-[10px]">
                      {d.batch_count} batch{d.batch_count === 1 ? '' : 'es'}
                    </Badge>
                  )}
                </div>
                {d.description && (
                  <p className="text-xs text-muted-foreground truncate">{d.description}</p>
                )}
              </div>
              {canEdit && (
                <div className="flex items-center gap-1">
                  <Button size="icon" variant="ghost" onClick={() => moveRow(idx, -1)}
                          disabled={idx === 0 || reorderBusy} aria-label="Move up"
                          data-testid={`department-up-${d.department_id}`}>
                    <ChevronUp className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => moveRow(idx, 1)}
                          disabled={idx === departments.length - 1 || reorderBusy} aria-label="Move down"
                          data-testid={`department-down-${d.department_id}`}>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => toggleActive(d)}
                          title={d.is_active ? 'Deactivate' : 'Activate'}
                          data-testid={`department-toggle-${d.department_id}`}>
                    <Power className={`w-4 h-4 ${d.is_active ? 'text-emerald-600' : 'text-muted-foreground'}`} />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => openEdit(d)}
                          data-testid={`department-edit-${d.department_id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => setConfirmDelete(d)}
                          data-testid={`department-delete-${d.department_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </Card>
      )}

      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent data-testid="department-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.name}` : 'New department'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label>Name *</Label>
                <Input value={form.name}
                       onChange={e => setForm({ ...form, name: e.target.value })}
                       placeholder="e.g. Revenue Outbound"
                       data-testid="department-name-input" />
              </div>
              <div>
                <Label>Code *</Label>
                <Input value={form.code}
                       onChange={e => setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                       placeholder="ROB" maxLength={8}
                       data-testid="department-code-input" />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} rows={2}
                        onChange={e => setForm({ ...form, description: e.target.value })}
                        placeholder="Optional context for trainers"
                        data-testid="department-description-input" />
            </div>
            <p className="text-xs text-muted-foreground">
              The code prefixes auto-generated batch codes — e.g. <code>{(form.code || 'XXX')}-B1</code>.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button onClick={onSave} data-testid="department-save-btn">
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete department?</AlertDialogTitle>
            <AlertDialogDescription>
              Removes <strong>{confirmDelete?.name}</strong>. If any batch is still in this department,
              the delete will be blocked — deactivate it to hide it from the picker without losing data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} data-testid="confirm-delete-department-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
